import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import dayjs from 'dayjs';

/**
 * InlineEdit — Klick auf den Wert öffnet die Bearbeitung, gespeichert wird
 * automatisch (Auto-Save, v2.4):
 *   - Textfelder: Enter oder Klick außerhalb speichert, Esc bricht ab.
 *   - Mehrzeilig: Klick außerhalb speichert, Esc bricht ab (Enter = neue Zeile).
 *   - Auswahlfelder: öffnen sich sofort, die Auswahl speichert direkt.
 * Keine Speichern-/Abbrechen-Knöpfe mehr — nichts kann verrutschen; die
 * Snackbar der Elternkomponente bestätigt den Erfolg. Unverändertes wird
 * nicht gespeichert (kein Request, keine Snackbar).
 *
 * @param {Object} props
 * @param {string} props.value - Der aktuelle Wert des Feldes
 * @param {Function} props.onSave - Callback beim Speichern (value) => Promise
 * @param {string} props.type - Typ des Input-Feldes ('text', 'number', 'date')
 * @param {boolean} props.multiline - Mehrzeiliges Textfeld
 * @param {number} props.rows - Zeilen bei multiline
 * @param {Array} props.selectOptions - Optionen für Select [{value, label}]
 * @param {string} props.placeholder - Platzhalter-Text
 * @param {Function} props.formatDisplay - Formatierung der Anzeige
 * @param {boolean} props.disabled - Deaktiviert die Bearbeitung
 * @param {Object} props.sx - Zusätzliche Styles für die Anzeige-Box
 * @param {Object} props.textFieldProps - Zusätzliche Props für TextField
 * @param {Function} props.validate - (value) => Fehlertext | null
 */
const InlineEdit = ({
  value,
  onSave,
  type = 'text',
  multiline = false,
  rows = 1,
  selectOptions = null,
  placeholder = '',
  formatDisplay = null,
  disabled = false,
  sx = {},
  textFieldProps = {},
  validate = null,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState(null);
  // Verhindert Doppel-Speichern (Enter + Blur) bzw. Speichern nach Esc
  const settledRef = useRef(false);

  const handleStartEdit = () => {
    if (disabled) return;
    settledRef.current = false;
    setValidationError(null);
    setEditValue(value);
    setIsEditing(true);
  };

  const handleCancel = () => {
    settledRef.current = true;
    setIsEditing(false);
    setEditValue(value);
    setValidationError(null);
  };

  // Speichert den übergebenen Wert (Auto-Save bei Blur/Enter/Auswahl).
  const commit = async (nextValue) => {
    if (settledRef.current) return;
    settledRef.current = true;

    const unchanged = (nextValue ?? '') === (value ?? '');
    if (unchanged) {
      setIsEditing(false);
      return;
    }
    if (validate) {
      const error = validate(nextValue);
      if (error) {
        // Ungültiges wird nicht gespeichert — zurück zur Anzeige mit altem Wert.
        setIsEditing(false);
        setEditValue(value);
        setValidationError(null);
        return;
      }
    }
    setIsSaving(true);
    try {
      await onSave(nextValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      // Fehler zeigt die Elternkomponente (Snackbar) — zurück zum alten Wert.
      setEditValue(value);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      commit(editValue);
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const getDisplayValue = () => {
    if (formatDisplay) {
      return formatDisplay(value);
    }

    if (selectOptions) {
      if (!value) return '';

      const option = selectOptions.find(opt => opt.value === value);
      return option?.label || value;
    }

    if (type === 'date') {
      return dayjs(value).format('MMM D, YYYY');
    }

    return value || placeholder;
  };

  if (isEditing) {
    return (
      <Box sx={{ minWidth: 0, width: '100%' }}>
        {selectOptions ? (
          <FormControl size="small" fullWidth>
            <Select
              value={editValue || ''}
              autoFocus
              defaultOpen
              disabled={isSaving}
              onChange={(e) => {
                setEditValue(e.target.value);
                commit(e.target.value); // Auswahl speichert direkt
              }}
              onClose={() => {
                // Zugeklappt ohne Auswahl → Bearbeitung still beenden
                setTimeout(() => {
                  if (!settledRef.current) handleCancel();
                }, 0);
              }}
            >
              {selectOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.isEmpty ? (
                    <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      {option.label}
                    </Box>
                  ) : (
                    option.label
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            type={type}
            value={editValue !== undefined && editValue !== null ? editValue : ''}
            onChange={(e) => {
              setEditValue(e.target.value);
              if (validate) setValidationError(validate(e.target.value));
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => commit(editValue)}
            size="small"
            multiline={multiline}
            rows={multiline ? rows : 1}
            autoFocus
            fullWidth
            disabled={isSaving}
            InputLabelProps={type === 'date' ? { shrink: true } : {}}
            error={!!validationError}
            helperText={validationError || (multiline ? 'Klick außerhalb speichert · Esc bricht ab' : undefined)}
            {...textFieldProps}
          />
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': disabled ? {} : {
          backgroundColor: 'action.hover',
          borderRadius: 1,
        },
        ...sx
      }}
      onClick={handleStartEdit}
    >
      {/* Text */}
      <Typography
        variant="inherit"
        sx={{
          whiteSpace: multiline ? 'pre-wrap' : 'normal',
          color: !value && placeholder ? 'text.secondary' : 'inherit',
          flex: 1,
        }}
      >
        {getDisplayValue()}
      </Typography>

      {/* Pfeil- bzw. Stift-Hinweis */}
      {!disabled && (
        selectOptions ? (
          <ArrowDropDownIcon
            sx={{
              fontSize: 20,
              color: 'action.active',
              opacity: 0.7,
              ml: 1,
            }}
          />
        ) : (
          <EditIcon
            sx={{
              fontSize: 16,
              color: 'action.active',
              opacity: 0.6,
              ml: 1,
            }}
          />
        )
      )}
    </Box>
  );

};

export default InlineEdit;
